import { ComponentType, ReactNode } from 'react';

import {
  AlertTriangleIcon,
  Announcement01Icon,
  ArrowRightIcon,
  CheckCircleIcon,
  Hash02Icon,
  Hourglass03Icon,
  LinkBroken01Icon,
  SlashCircle02Icon,
  Tool02Icon,
  UserX02Icon,
} from '@/assets/icons';
import { IBadge, SvgIcon, Tooltip, TooltipContent, TooltipTrigger } from '@/components/common';

import {
  Classification,
  JobStatus,
  JobTrigger,
  NotRegulatoryReason,
  ProcessingStatus,
} from '@/features/regulatory-intake/enums';

export const getProcessingStatusLabel = (status: ProcessingStatus): string => {
  switch (status) {
    case ProcessingStatus.NotAvailable:
      return 'N/A';
    case ProcessingStatus.Queued:
      return 'Queued';
    case ProcessingStatus.Processing:
      return 'Processing';
    case ProcessingStatus.Transferring:
      return 'Transferring';
    case ProcessingStatus.Transferred:
      return 'Transferred';
    case ProcessingStatus.Attention:
      return 'Attention';
    case ProcessingStatus.Failed:
      return 'Failed';
    default:
      return 'N/A';
  }
};

export const getJobStatusLabel = (status: JobStatus): string => {
  switch (status) {
    case JobStatus.NotAvailable:
      return 'N/A';
    case JobStatus.Running:
      return 'Running';
    case JobStatus.Completed:
      return 'Completed';
    case JobStatus.CompletedWithErrors:
      return 'Completed with Errors';
    case JobStatus.Failed:
      return 'Failed';
    default:
      return 'N/A';
  }
};

export const getTriggerLabel = (trigger: JobTrigger): string => {
  switch (trigger) {
    case JobTrigger.Manual:
      return 'Manual';
    case JobTrigger.System:
      return 'Scheduled';
    default:
      return 'Scheduled';
  }
};

export const getClassificationLabel = (
  classification: Classification,
  reason?: NotRegulatoryReason,
): ReactNode => {
  switch (classification) {
    case Classification.NotRegulatory:
      return (
        <span>
          Not regulatory
          <Tooltip>
            <TooltipTrigger className="cursor-default align-middle ml-2">
              <SvgIcon
                component={getNotRegulatoryReasonIcon(reason!)}
                className="size-4 text-neutral-7"
              />
            </TooltipTrigger>
            <TooltipContent className="text-body-sm font-medium rounded-xs" size="lg" side="bottom">
              {getNotRegulatoryReasonTooltip(reason!)}
            </TooltipContent>
          </Tooltip>
        </span>
      );
    case Classification.Regulatory:
      return 'Regulatory notification';
    default:
      return '';
  }
};

const getNotRegulatoryReasonIcon = (
  reason: NotRegulatoryReason,
): ComponentType<{ className?: string }> => {
  switch (reason) {
    case NotRegulatoryReason.UntrustedSender:
      return UserX02Icon;
    case NotRegulatoryReason.UntrustedSource:
      return LinkBroken01Icon;
    case NotRegulatoryReason.Bounce:
    case NotRegulatoryReason.SubscriptionMessage:
      return Announcement01Icon;
    default:
      return UserX02Icon;
  }
};

const getNotRegulatoryReasonTooltip = (reason: NotRegulatoryReason): string => {
  switch (reason) {
    case NotRegulatoryReason.UntrustedSender:
      return 'Sender not the authority';
    case NotRegulatoryReason.UntrustedSource:
      return "No link to the authority's site";
    case NotRegulatoryReason.Bounce:
    case NotRegulatoryReason.SubscriptionMessage:
      return 'Bounce or subscription message';
    default:
      return 'Sender not the authority';
  }
};

export const getProcessingStatusVariant = (status: ProcessingStatus): IBadge['variant'] => {
  switch (status) {
    case ProcessingStatus.NotAvailable:
      return 'neutral-3';
    case ProcessingStatus.Queued:
      return 'yellow';
    case ProcessingStatus.Processing:
      return 'blue';
    case ProcessingStatus.Transferring:
      return 'blue';
    case ProcessingStatus.Transferred:
      return 'green';
    case ProcessingStatus.Attention:
      return 'red';
    case ProcessingStatus.Failed:
      return 'red';
    default:
      return 'neutral-3';
  }
};

export const getJobStatusVariant = (status: JobStatus): IBadge['variant'] => {
  switch (status) {
    case JobStatus.NotAvailable:
      return 'neutral-3';
    case JobStatus.Running:
      return 'blue';
    case JobStatus.Completed:
      return 'green';
    case JobStatus.CompletedWithErrors:
      return 'yellow';
    case JobStatus.Failed:
      return 'red';
    default:
      return 'neutral-3';
  }
};

export const getProcessingStatusIcon = (
  status: ProcessingStatus,
): ComponentType<{ className?: string }> => {
  switch (status) {
    case ProcessingStatus.NotAvailable:
      return Hash02Icon;
    case ProcessingStatus.Queued:
      return Hourglass03Icon;
    case ProcessingStatus.Processing:
      return Tool02Icon;
    case ProcessingStatus.Transferring:
      return ArrowRightIcon;
    case ProcessingStatus.Transferred:
      return CheckCircleIcon;
    case ProcessingStatus.Attention:
      return AlertTriangleIcon;
    case ProcessingStatus.Failed:
      return SlashCircle02Icon;
    default:
      return Hash02Icon;
  }
};

export const getJobStatusIcon = (status: JobStatus): ComponentType<{ className?: string }> => {
  switch (status) {
    case JobStatus.NotAvailable:
      return Hash02Icon;
    case JobStatus.Failed:
      return SlashCircle02Icon;
    case JobStatus.Running:
      return Tool02Icon;
    case JobStatus.Completed:
      return CheckCircleIcon;
    case JobStatus.CompletedWithErrors:
      return AlertTriangleIcon;
    default:
      return Hash02Icon;
  }
};
